<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Display
  Reason: Read-only processing panel of a pd.PdPartner — lock state, delta watermark, last collection run and last
  export run (each with loading / empty / error state and a link into the generic object view).
-->
@if (partner) {
    <div class="flex flex-column gap-3 p-3 surface-card border-1 surface-border border-round-lg">

        <!-- Lock and watermark -->
        <div class="flex align-items-center justify-content-between gap-2">
            <span class="text-lg font-semibold">Verarbeitung</span>
            <span [class]="processingStatusChipClass">{{ processingStatusLabel }}</span>
        </div>

        <div class="flex flex-column gap-2 text-sm">
            <div class="flex justify-content-between gap-3">
                <span class="text-600">Laufender Job</span>
                <span class="font-medium text-right">{{ isLocked && processingKindLabel ? processingKindLabel : '–' }}</span>
            </div>
            <div class="flex justify-content-between gap-3">
                <span class="text-600">Gesperrt seit</span>
                <span class="font-medium text-right">{{ isLocked ? (formatDateTime(partner.processingStartedAt) || '–') : '–' }}</span>
            </div>
            @if (isLocked && partner.processingJobRunDtoId) {
                <div class="flex justify-content-between gap-3">
                    <span class="text-600">Sperrender Joblauf</span>
                    <span class="font-medium text-right">#{{ partner.processingJobRunDtoId }}</span>
                </div>
            }
            <div class="flex justify-content-between gap-3">
                <span class="text-600">Letzter Delta-Zeitpunkt</span>
                <span class="font-medium text-right">{{ formatDateTime(partner.lastDeltaTimestamp) || 'Noch kein erfolgreicher Sammellauf' }}</span>
            </div>
        </div>

        <p-divider class="my-1"></p-divider>

        <!-- Last collection run -->
        <div class="flex flex-column gap-2">
            <div class="flex align-items-center justify-content-between gap-2">
                <span class="font-semibold">Letzter Sammellauf</span>
                @if (collectionRunState === 'ready' && collectionRun) {
                    <p-button label="Öffnen"
                              icon="fa-regular fa-arrow-up-right-from-square"
                              iconPos="right"
                              size="small"
                              [link]="true"
                              (onClick)="openCollectionRun()"></p-button>
                }
            </div>
            @switch (collectionRunState) {
                @case ('loading') {
                    <p-skeleton height="2.5rem" borderRadius="0.5rem"></p-skeleton>
                }
                @case ('error') {
                    <span class="text-sm text-red-600">Der letzte Sammellauf konnte nicht geladen werden.</span>
                }
                @case ('ready') {
                    @if (collectionRun) {
                        <div class="flex flex-wrap align-items-center gap-2">
                            <span [class]="runStatusChipClass(collectionRun.status)">{{ runStatusLabel(collectionRun.status) }}</span>
                            @if (collectionModeLabel(collectionRun)) {
                                <span [class]="chipClass('neutral')">{{ collectionModeLabel(collectionRun) }}</span>
                            }
                            @if (collectionRun.dryRun) {
                                <span [class]="chipClass('info')">Probelauf</span>
                            }
                        </div>
                        <div class="flex flex-column gap-1 text-sm">
                            <span class="text-600">Gestartet am {{ formatDateTime(collectionRun.startedAt) || '–' }}</span>
                            @if (collectionRun.finishedAt) {
                                <span class="text-600">Beendet am {{ formatDateTime(collectionRun.finishedAt) }}</span>
                            }
                            <span>
                                {{ collectionRun.eventsCreated ?? 0 }} angelegt · {{ collectionRun.eventsObsoleted ?? 0 }} überholt
                                · {{ collectionRun.warnings ?? 0 }} Warnungen
                            </span>
                        </div>
                    }
                }
                @default {
                    <span class="text-sm text-600">Noch kein Sammellauf. Starten Sie einen Probelauf, um die Konfiguration zu prüfen.</span>
                }
            }
        </div>

        <p-divider class="my-1"></p-divider>

        <!-- Last export run -->
        <div class="flex flex-column gap-2">
            <div class="flex align-items-center justify-content-between gap-2">
                <span class="font-semibold">Letzter Exportlauf</span>
                @if (exportRunState === 'ready' && exportRun) {
                    <p-button label="Öffnen"
                              icon="fa-regular fa-arrow-up-right-from-square"
                              iconPos="right"
                              size="small"
                              [link]="true"
                              (onClick)="openExportRun()"></p-button>
                }
            </div>
            @switch (exportRunState) {
                @case ('loading') {
                    <p-skeleton height="2.5rem" borderRadius="0.5rem"></p-skeleton>
                }
                @case ('error') {
                    <span class="text-sm text-red-600">Der letzte Exportlauf konnte nicht geladen werden.</span>
                }
                @case ('ready') {
                    @if (exportRun) {
                        <div class="flex flex-wrap align-items-center gap-2">
                            <span [class]="runStatusChipClass(exportRun.status)">{{ runStatusLabel(exportRun.status) }}</span>
                            @if (exportChannelLabel(exportRun)) {
                                <span [class]="chipClass('neutral')">{{ exportChannelLabel(exportRun) }}</span>
                            }
                        </div>
                        <div class="flex flex-column gap-1 text-sm">
                            <span class="text-600">Gestartet am {{ formatDateTime(exportRun.startedAt) || '–' }}</span>
                            @if (exportRun.finishedAt) {
                                <span class="text-600">Beendet am {{ formatDateTime(exportRun.finishedAt) }}</span>
                            }
                            <span>
                                {{ exportRun.eventsSent ?? 0 }} von {{ exportRun.eventsSelected ?? 0 }} versendet
                                · {{ exportRun.eventsFailed ?? 0 }} fehlgeschlagen
                            </span>
                        </div>
                    }
                }
                @default {
                    <span class="text-sm text-600">
                        Noch kein Export. Ein Export gibt die offenen Ereignisse über den Exportkanal aus – starten
                        Sie ihn über „Export starten“.
                    </span>
                }
            }
        </div>
    </div>
}
